import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCXone } from '../contexts/CXoneContext';
import { useCXoneEvents } from '../hooks/useCXoneEvents';
import { useAgentWidgetIntegration } from '../hooks/useAgentWidgetIntegration';
import { performOutboundDial } from '../services/sap-context/outbound-dial.util';
import { getPendingInboundContactId } from '../services/cxone-sdk.service';
import { isAcwUiLocked, shouldIgnoreStaleLiveCall } from '../hooks/agent-widget/acw-ui-lock';
import { resolveIsInAcwState } from '../hooks/agent-widget/acw-detection.util';
import { isLiveCallStatus, resolveWidgetViews } from './agent-widget/widget-view-routing.util';
import { logger } from '../utils/logger';
import { Layout } from './layout/Layout';
import { AgentWidgetGate } from './agent-widget/AgentWidgetGate';
import { AgentWidgetHeaderActions } from './agent-widget/AgentWidgetHeaderActions';
import { AgentWidgetContent } from './agent-widget/AgentWidgetContent';
import { CallHistoryDrawer } from './activity-log/CallHistoryDrawer';
import { useSettingsMenu } from './agent-widget/useSettingsMenu';

export function AgentWidget() {
  const { user, logout } = useAuth();
  const {
    isInitialized,
    isConnecting,
    isConnected,
    connectionError,
    connectionProgress,
    hasSessionConflict,
    needsVoicePreference,
    isSessionInvalid,
    isReconnecting,
    initializeWithVoicePreference,
    forceLogoutAndReconnect,
    clearSessionConflict,
    reconnect,
  } = useCXone();
  const {
    callState,
    agentState,
    unavailableCodes,
    isRinging,
    isMuted,
    isRecording,
    isAgentLegActive,
    callError,
    acwTimeout,
    isInAcw,
    acceptCall,
    rejectCall,
    setAgentState,
  } = useCXoneEvents();

  const {
    sapContactState,
    isSapC4cEnabled,
    manualSearchState,
    openedSapContacts,
    showACW,
    lastCallInfo,
    conferenceState,
    handleACWComplete,
    handleCallEnd,
    handleTransferComplete,
    handleConferenceInitiate,
    handleSapContactOpened,
    handleManualSearch,
    isOutboundCall,
    outboundCallInfo,
  } = useAgentWidgetIntegration({
    callState,
    isRinging,
    agentState,
    unavailableCodes,
    isInAcw,
    setAgentState,
  });

  const {
    showSettingsMenu,
    setShowSettingsMenu,
    showMicSettings,
    showProfile,
    openMicSettings,
    openProfile,
    closeMicSettings,
    closeProfile,
    settingsRef,
  } = useSettingsMenu();

  const [showCallHistory, setShowCallHistory] = useState(false);

  const handleOpenCallHistory = useCallback(() => {
    setShowSettingsMenu(false);
    setShowCallHistory(true);
  }, [setShowSettingsMenu]);

  const handleCloseCallHistory = useCallback(() => {
    setShowCallHistory(false);
  }, []);

  const handleStateChange = useCallback(
    async (state: string, reasonCode?: string) => {
      if (state?.toLowerCase() === 'available' && (showACW || isInAcw)) {
        logger.lifecycle('[ACW Flow] header Available during ACW — running completion');
        await handleACWComplete({});
        return;
      }
      await setAgentState(state, reasonCode);
    },
    [setAgentState, showACW, isInAcw, handleACWComplete]
  );

  const handleManualDial = useCallback(async (phoneNumber: string) => {
    await performOutboundDial(phoneNumber);
  }, []);

  const isSettingsOpen = showMicSettings || showProfile;
  const agentInAcw = resolveIsInAcwState(isInAcw, agentState, unavailableCodes);
  const pendingContactId = getPendingInboundContactId();
  const staleLiveCall =
    shouldIgnoreStaleLiveCall(callState.contactId ?? pendingContactId) &&
    isLiveCallStatus(callState.status, isRinging);
  const { isCallView, isIdleView, effectiveShowACW, isOutboundBootstrap } = resolveWidgetViews({
    callState,
    isRinging,
    showACW,
    agentInAcw,
    isOutboundCall,
    pendingContactId,
    acwLocked: isAcwUiLocked(),
    staleLiveCall,
    isSettingsOpen,
  });

  useEffect(() => {
    logger.lifecycle('[ACW Flow] view routing', {
      effectiveShowACW,
      showACW,
      isOutboundBootstrap,
      isOutboundCall,
      agentInAcw,
      isIdleView,
      isCallView,
      callStatus: callState.status,
      isRinging,
      sdkIsInAcw: isInAcw,
      agentState: agentState.state,
      agentReason: agentState.reason,
      acwLocked: isAcwUiLocked(),
      staleLiveCall,
      pendingContactId,
    });
  }, [
    effectiveShowACW,
    showACW,
    isOutboundBootstrap,
    isOutboundCall,
    agentInAcw,
    isIdleView,
    isCallView,
    callState.status,
    isRinging,
    isInAcw,
    agentState.state,
    agentState.reason,
    staleLiveCall,
    pendingContactId,
  ]);

  return (
    <AgentWidgetGate
      isConnecting={isConnecting}
      connectionProgress={connectionProgress}
      hasSessionConflict={hasSessionConflict}
      needsVoicePreference={needsVoicePreference}
      isSessionInvalid={isSessionInvalid}
      connectionError={connectionError}
      isInitialized={isInitialized}
      isReconnecting={isReconnecting}
      forceLogoutAndReconnect={forceLogoutAndReconnect}
      clearSessionConflict={clearSessionConflict}
      initializeWithVoicePreference={initializeWithVoicePreference}
      reconnect={reconnect}
    >
      <Layout
        headerActions={
          <AgentWidgetHeaderActions
            agentState={agentState}
            unavailableCodes={unavailableCodes}
            callState={callState}
            user={user}
            isConnected={isConnected}
            settingsRef={settingsRef}
            showSettingsMenu={showSettingsMenu}
            onToggleSettingsMenu={() => setShowSettingsMenu(!showSettingsMenu)}
            onCloseSettingsMenu={() => setShowSettingsMenu(false)}
            onOpenMicSettings={openMicSettings}
            onOpenProfile={openProfile}
            onOpenCallHistory={handleOpenCallHistory}
            onStateChange={handleStateChange}
            onLogout={logout}
          />
        }
      >
        <AgentWidgetContent
          showMicSettings={showMicSettings}
          onCloseMicSettings={closeMicSettings}
          showProfile={showProfile}
          onCloseProfile={closeProfile}
          isIdleView={isIdleView}
          isCallView={isCallView}
          showACW={effectiveShowACW}
          agentState={agentState}
          callState={callState}
          conferenceState={conferenceState}
          isRinging={isRinging}
          isMuted={isMuted}
          isRecording={isRecording}
          isAgentLegActive={isAgentLegActive}
          callError={callError}
          isSapC4cEnabled={isSapC4cEnabled}
          sapContactState={sapContactState}
          manualSearchState={manualSearchState}
          openedSapContacts={openedSapContacts}
          acwTimeout={acwTimeout}
          lastCallInfo={lastCallInfo}
          callDisplayContext={{
            isOutboundCall,
            outboundPhone: outboundCallInfo?.phone ?? null,
            outboundReferenceId: outboundCallInfo?.referenceId ?? null,
          }}
          onAccept={acceptCall}
          onReject={rejectCall}
          onTransfer={handleTransferComplete}
          onConference={handleConferenceInitiate}
          onCallEnd={handleCallEnd}
          onContactOpened={handleSapContactOpened}
          onManualSearch={handleManualSearch}
          onManualDial={handleManualDial}
          onAcwComplete={handleACWComplete}
        />
      </Layout>
      {showCallHistory && <CallHistoryDrawer onClose={handleCloseCallHistory} />}
    </AgentWidgetGate>
  );
}

export default AgentWidget;
